// Core angular
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Observable, of } from 'rxjs';

// Librerías de terceros
import { TranslateLoader as NgxTranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerModule } from 'ngx-spinner';
import { AccordionModule } from 'primeng/accordion';

// Providers
import { DataProvider } from './providers/data.provider';

// Servicios
import { RequestsService } from './services/requests.service';
import { UtilitatsService } from './services/utilitats.service';
import { ModalService } from './services/modal.service';
import { RumiService } from './services/rumi.service';
import { ConfirmService } from './services/confirm.service';

// Routing module
import { AppRoutingModule } from './app-routing.module';

// Componentes
import { AppComponent } from './app.component';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { HomeComponent } from './components/home/home.component';
import { IniciSimulacioComponent } from './components/simulacio/inici-simulacio/inici-simulacio.component';
import { QuestionariObservacionsComponent } from './components/simulacio/questionari-observacions/questionari-observacions.component';
import { ApreciacioGravetatComponent } from './components/simulacio/apreciacio-gravetat/apreciacio-gravetat.component';
import { ValoracionsFinalsComponent } from './components/simulacio/valoracions-finals/valoracions-finals.component';
import { AltresObservacionsComponent } from './components/simulacio/altres-observacions/altres-observacions.component';
import { ResultatRiscComponent } from './components/simulacio/resultat-risc/resultat-risc.component';
import { AccionsComponent } from './components/simulacio/accions/accions.component';
import { ModalComponent } from './components/modal/modal.component';
import { PanelComponent } from './components/panel/panel.component';
import { AccordionComponent } from './components/accordion/accordion.component';
import { SimulacioComponent } from './components/simulacio/simulacio.component';
import { AjudaComponent } from './components/ajuda/ajuda.component';
import { ConfirmComponent } from './components/confirm/confirm.component';

// Directives
import { DraggableDirective } from './directives/draggable.directive';

// Recursos del lenguaje
import { translationCaEs } from '../assets/i18n/ca_ES';
import { translationEnGb } from '../assets/i18n/en_GB';
import { translationEsEs } from '../assets/i18n/es_ES';
import { translationOcEs } from '../assets/i18n/oc_ES';

const TRANSLATIONS = {
  ca_ES: translationCaEs,
  es_ES: translationEsEs,
  oc_ES: translationOcEs,
  en_GB: translationEnGb
};

export class TranslateLoader implements NgxTranslateLoader {
  public getTranslation(lang: string): Observable<any> {
    return of(TRANSLATIONS[lang]);
  }
}

export function translateFactory() {
  return new TranslateLoader();
}

// Funció que precarrega les dades del provider
export function DataProviderFactory(provider: DataProvider) {
  return () => provider.load();
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    ModalComponent,
    HomeComponent,
    IniciSimulacioComponent,
    QuestionariObservacionsComponent,
    ApreciacioGravetatComponent,
    ValoracionsFinalsComponent,
    AltresObservacionsComponent,
    ResultatRiscComponent,
    AccionsComponent,
    PanelComponent,
    AccordionComponent,
    SimulacioComponent,
    AjudaComponent,
    ConfirmComponent,
    DraggableDirective
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: NgxTranslateLoader,
        useFactory: translateFactory
      }
    }),
    NgxSpinnerModule,
    AccordionModule
  ],
  providers: [
    RequestsService,
    DataProvider,
    { provide: APP_INITIALIZER, useFactory: DataProviderFactory, deps: [DataProvider], multi: true },
    UtilitatsService,
    ModalService,
    RumiService,
    ConfirmService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
